// WebAuthn wire-format helpers.
//
// The service speaks base64url over JSON while `navigator.credentials` speaks
// ArrayBuffer, so every registration crosses this boundary twice: decode what
// `beginAuthRegistration` sends, encode what the authenticator returns. The
// service is strict about the shape it receives — an attestation missing a
// field, or padded/`+/`-flavoured base64, is rejected after the user has
// already touched their key, which reads to them as a broken screen.

/** base64url (no padding, -_ alphabet) -> Uint8Array. */
export const base64UrlToUint8Array = (value) => {
  const base64 = String(value ?? "").replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/** ArrayBuffer / TypedArray -> base64url (no padding). */
export const bufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

/**
 * The `publicKey` block from beginAuthRegistration, with every base64url field
 * decoded to the Uint8Array `navigator.credentials.create()` requires.
 *
 * `excludeCredentials` carries the credentials this user already registered —
 * it is what makes re-registering the same authenticator fail locally with an
 * InvalidStateError instead of silently creating a duplicate. Leaving its ids
 * as strings does not error; it produces an empty exclude list, so the
 * duplicate goes through. Decode it, and pass the array through only when the
 * service sent one.
 */
export const toCredentialCreationOptions = (publicKey) => {
  const options = {
    ...publicKey,
    challenge: base64UrlToUint8Array(publicKey.challenge),
    user: { ...publicKey.user, id: base64UrlToUint8Array(publicKey.user?.id) },
  };
  if (Array.isArray(publicKey.excludeCredentials)) {
    options.excludeCredentials = publicKey.excludeCredentials.map((c) => ({
      ...c,
      id: base64UrlToUint8Array(c.id),
    }));
  }
  return options;
};

/** The attestation body finishRegistration expects, exactly these fields. */
export const toAttestationPayload = (credential) => ({
  id: credential.id,
  rawId: bufferToBase64Url(credential.rawId),
  type: "public-key",
  response: {
    attestationObject: bufferToBase64Url(credential.response.attestationObject),
    clientDataJSON: bufferToBase64Url(credential.response.clientDataJSON),
  },
});

/** Passkeys need both an implementing browser and a secure context. */
export const passkeysAvailable = () =>
  typeof window !== "undefined" &&
  typeof window.PublicKeyCredential !== "undefined" &&
  window.isSecureContext !== false;
